<template>
    <modals-side-generic
    helperTextTitle
    emitLeftButtonText
    :title="headerTitle"
    :headerLeftButtonText="helperStep === 0 ? null : t(`modals.backToHelperIndex`)"
    :showFooter="helperStep > 0"
    :buttonsFooterData="buttonsFooterData"
    :sizeClass="sizeClass"
    :triggerClose="triggerClose"
    @buttonLeftClicked="helperStep = 0"
    @hideMe="emit('hideMe')">
        <!-- *********************************************
        *  	                   Content                	 *
        ********************************************** -->
        <component
        :is="stepComponent"
        :helpTopics="helperTopics"
        :currentAnchorPoint="currentAnchorPoint"
        @scrollMeTop="scrollMeTopHandler"
        @scrollToAnchor="currentAnchorPoint = $event"
        @updateAnchorPointPosition="scrollToAnchorHandler"
        @helperStepUpdate="helperStep = $event"/>
    </modals-side-generic>
</template>

<script setup>
import ModalsSideGeneric from '@/components/generic/modals/side/Generic'

/* Vue && Assets */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'
import { scrollTo } from '@/assets/scripts/scrollAnimation'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'headerButtonClicked'])

/* Props
@titlesArray {Array} - List of text to display in title
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
@helpTopics {Array} - List of text to display in index component (opcional, if none are passed it will use titlesArray values)
@helperStepComponentsFolder {Boolean} - Folder name where helper steps are for specific feature
@initialHelperStep {Number} - Initial step to mount modal content component
*/
const props = defineProps({
    titlesArray: String,
    triggerClose: Boolean,
    helpTopics: Array,
    helperStepComponentsFolder: String,
    initialHelperStep: Number
})

const { updatePreventMobileFeatureBack } = mapActions()
const { getPreventMobileSideModalBackState } = mapGetters()

const helperTopics = ref(props.helpTopics || props.titlesArray)
const showModalWrapper = ref(false)
const modalContent = ref('')
const currentAnchorPoint = ref(null)
const helperStep = ref(props.initialHelperStep || 0)

const headerTitle = computed(() => {
    if (helperStep.value === 0) return t('modals.helperTitle')

    return props.titlesArray[helperStep.value - 1]
})

const stepComponent = computed(() => {
    if (helperStep.value === 0) return defineAsyncComponent(() => import('@/components/generic/modals/side/helper/Index'))

    return defineAsyncComponent(() => import('@/components/' + props.helperStepComponentsFolder + '/Step' + helperStep.value))
})

const validateIfClose = $event => {
    const className = 'dont-close-side-modals'
    const eventHasClass = $event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className)

    return validateIfParentExists($event.target, className) || eventHasClass
}

const buttonsFooterData = computed(() => {
    let buttonData = []

    buttonData = [{
        key: 1,
        label: t('modals.previousTopic'),
        icon: 'arrow-left-full',
        buttonPosition: 'left',
        callbackFunction: () => {
            helperStep.value--
            currentAnchorPoint.value = null
        }
    },
    {
        key: 2,
        label: t('modals.nextTopic'),
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        callbackFunction: () => {
            helperStep.value++
            currentAnchorPoint.value = null
        }
    }]

    if (helperStep.value === 1) buttonData.shift()

    if (helperStep.value === helperTopics.value.length) buttonData.pop()

    return buttonData
})

const scrollToAnchorHandler = event => {
    scrollTo(modalContent.value, (event - 14), null, 500, 'vertical') // 14 is the margin for the text don't touch the modalHeader
}

const scrollMeTopHandler = () => {
    scrollTo(modalContent.value, 0, null, 500, 'vertical')
}

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
})

const onBackKeyDown = () => {
    if (getPreventMobileSideModalBackState.value) return
    showModalWrapper.value = false
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.help-step-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 24px;

    @include H5-Regular;
    color: var(--secondary-dark-3);
    line-height: 20px;
}
</style>
